import { useState, useEffect, useCallback } from 'react';
import notify, { MESSAGES } from '../../../utils/notify';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import { pick } from '../../../shared/offer-domain/pick';

const normalizeClarificationBidder = raw => {
  if (!raw || typeof raw !== 'object') return null;

  return {
    id: pick(raw, 'id', 'Id'),
    companyName: pick(raw, 'companyName', 'CompanyName'),
    statusClarification: pick(
      raw,
      'statusClarification',
      'StatusClarification'
    ),
    statusClarificationLabel: pick(
      raw,
      'statusClarificationLabel',
      'StatusClarificationLabel'
    ),
    statusClarificationProgress: pick(
      raw,
      'statusClarificationProgress',
      'StatusClarificationProgress'
    ),
    statusClarificationProgressLabel: pick(
      raw,
      'statusClarificationProgressLabel',
      'StatusClarificationProgressLabel'
    ),
  };
};

// Rows come sorted by companyName from BE; do not re-sort (must not correlate with Offer order).
const useClarificationBidders = (
  procurementId,
  { enabled = true, isActive = true, refreshToken = 0 } = {}
) => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(
    Boolean(enabled && procurementId && isActive)
  );

  const fetchClarificationBidders = useCallback(
    async ({ silent = false } = {}) => {
      if (!procurementId || !enabled) return null;

      const token = getAuthToken();
      if (!token) {
        notify.error(MESSAGES.authRequired);
        return null;
      }

      if (!silent) setIsLoading(true);

      try {
        const url = `${api.API_URL}/procurement/${encodeURIComponent(procurementId)}/clarification-bidders`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'same-origin',
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const list = result?.data ?? result?.Data ?? [];
        const data = (Array.isArray(list) ? list : [])
          .map(normalizeClarificationBidder)
          .filter(Boolean);
        setItems(data);
        return data;
      } catch (err) {
        notify.apiError(err, 'load clarification bidder data');
        setItems([]);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [procurementId, enabled]
  );

  const refresh = useCallback(
    () => fetchClarificationBidders(),
    [fetchClarificationBidders]
  );

  useEffect(() => {
    if (!enabled || !procurementId || !isActive) return;
    fetchClarificationBidders();
  }, [
    enabled,
    procurementId,
    isActive,
    fetchClarificationBidders,
    refreshToken,
  ]);

  return {
    items,
    isLoading,
    refresh,
  };
};

export default useClarificationBidders;
